// src/components/platform/SprintRoadmap.tsx
import React from 'react';
import { motion } from 'framer-motion';
import clsx from 'clsx';
import { Check, Loader, Milestone } from 'lucide-react';

interface RoadmapItem {
  text: string;
}

interface RoadmapPhase {
  title: string;
  status: 'Complete' | 'In Progress' | 'Planned';
  items: RoadmapItem[];
}

interface SprintRoadmapProps {
  phases: RoadmapPhase[];
}

const statusConfig = {
  Complete: {
    icon: <Check className="text-emerald-500" />,
    color: 'border-emerald-500',
    textColor: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
  },
  'In Progress': {
    icon: <Loader className="text-sky-500 animate-spin" />,
    color: 'border-sky-500',
    textColor: 'text-sky-600',
    bgColor: 'bg-sky-50',
  },
  Planned: {
    icon: <Milestone className="text-slate-500" />,
    color: 'border-slate-400',
    textColor: 'text-slate-600',
    bgColor: 'bg-slate-50',
  },
};

export const SprintRoadmap: React.FC<SprintRoadmapProps> = ({ phases }) => {
  return (
    <div className="space-y-8">
      {phases.map((phase, index) => {
        const config = statusConfig[phase.status];
        return (
          <motion.div
            key={phase.title}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6, delay: index * 0.15 }}
            className={clsx('p-6 rounded-xl border-2', config.color, config.bgColor)}
          >
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0">{config.icon}</div>
              <div>
                <h3 className={clsx('text-xl font-bold', config.textColor)}>{phase.title}</h3>
                <p className="text-sm font-semibold">{phase.status}</p>
              </div>
            </div>
            <ul className="mt-4 pl-10 list-disc space-y-2 text-slate-700">
              {phase.items.map((item) => (
                <li key={item.text}>{item.text}</li>
              ))}
            </ul>
          </motion.div>
        );
      })}
    </div>
  );
};